// ============================================================================
// WorkspaceProjectTabs.tsx — the working-set rail down the left of QuoteMaker
// ----------------------------------------------------------------------------
// Operator request (Peter, 2026-08-19, with a drawing): the tabbed multi-quote
// view Import Projects gives the staging pipeline should also be switchable
// from INSIDE the QuoteMaker workspace.
//
// This is the third placement, and the reason it moved twice is worth keeping:
//
//   v1 — a 26px floating LEFT rail. Its labels clipped to "0…" over "QUE",
//        because the component computed a shortCode for that slot and then
//        rendered the full NAME into a ~14px content box that a 9px dot and an
//        8px gap had already overrun. It also used position:fixed and covered
//        the stage.
//   v2 — a horizontal shelf on the BottomNavBar. That fixed the clipping, but
//        threw away the thing the left edge was for: "with the tabs on the
//        bottom, i can't see the full text. that's why i imaged it on the left."
//   v3 — this: the LEFT rail done properly.
//
// The operator was right, and the arithmetic backs him rather than my first
// answer. Stacked vertically each job gets the rail's FULL width (~236px);
// horizontally, ten tabs split the viewport into ~140px each — and that gap
// widens as the count grows. Vertical is simply the better shape for names.
//
// It docks IN FLOW: verified by measuring the live DOM, the parent of the
// QuoteMaker main column (`flex-1 flex flex-col`) is `flex-1 flex
// overflow-hidden relative`, a flex ROW. So rendering this as that column's
// preceding sibling pushes the stage right instead of floating over it, with
// no fixed positioning and no CSS-variable reservation.
//
// A row has three deliberately different verbs, written out in the UI rather
// than hidden behind an ambiguous ×:
//   · HIDE FOR NOW only hides this rail item; a state change brings it back.
//   · TAKE OFF RUN moves a queued job back to its staged setup.
//   · REMOVE STAGING deletes that setup after a confirmation, while the
//     source files and any saved QuoteMaker project remain untouched.
// This separation is the safety model. A tiny close control must never look
// like permanent removal, and a permanent removal must never silently return
// when some unrelated job state changes.
//
// Derivation discipline unchanged: rows come from listJobs → collapseQueueJobs,
// the single derivation (import-workflow.ts) the queue drawer and staging sheet
// use, so this rail cannot disagree with Import Projects. Freshness rides
// useBatchJobsLiveSignal (Dexie liveQuery, cross-tab).
//
// Opening discipline unchanged: a click calls the App-supplied onOpenProject —
// App.jsx's guarded loadProject, which owns the unsaved-changes dialog and the
// opened-for-review semantics, so a switch cannot silently drop edits and never
// auto-fires the AI resume cascade.
//
// Kill switch: localStorage qm_disable_workspace_tabs = 'true'.
// ============================================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBatchJobsLiveSignal } from '../file-explorer/use-batch-jobs-live';
import { collapseQueueJobs } from '../file-explorer/import-workflow';
import type { ImportWorkflowState } from '../file-explorer/import-workflow';
import { Icons } from '../ui/Icon';
import ConfirmModal from '../modals/ConfirmModal';
import { assignRunPositions, railWordsFor } from './run-blocker';
import { QUOTE_RUN_DISPLAY, QUOTE_RUN_ENGINE_EVENTS, jobFolderName, jobQuoteTitle, quoteRunColours, quoteRunRedrawIn } from './quote-run-view';
import type { QuoteRunDisplay } from './quote-run-view';
import '../../styles/workspace-tabs.css';

type TabRow = {
    projectId: string;
    /** The engine's state, for the row's own actions (take off the run, remove staging). */
    state: ImportWorkflowState;
    /**
     * The view's display state (railWordsFor): what the rail shows, sorts,
     * counts and remembers a dismissal by, so a job whose page closed or is
     * being checked is never shown as running.
     */
    display: QuoteRunDisplay;
    /** The dot's colours, the view's. */
    palette: ImportWorkflowState;
    /** The folder name picked for the run, for the life of the row (G13). */
    name: string;
    /** The quote title once one is known, for the tooltip only. */
    quoteTitle: string;
    /** The distinctive part of the name — usually the place. */
    title: string;
    /** Reference codes lifted out of the name (strata plan, RFQ, job number). */
    ref: string;
    /** The ref ONLY when it is short enough to ride the title as a prefix. */
    shortRef: string;
    /** One short sentence: why this job is where it is. */
    status: string;
    /** The view's word for the job's state, the sheet's chip (railWordsFor). */
    chip: string;
    /** The row's one action on the sheet (railWordsFor): TAKE OFF RUN is the rail's Leave out. */
    actionKind: string | null;
    /** The collapsed queue row, kept so blockerFor sees the real job. */
    raw: any;
};

/**
 * What the hover text and the screen-reader label say about the job's state:
 * the view's chip with the status line, once each, no closing full stop. The
 * dot's colour is not an accessible signal, so the word rides the label.
 */
function railWords(row: TabRow): string {
    const status = row.status.trim();
    const chip = row.chip.trim();
    const said = !chip || status.toLowerCase().includes(chip.toLowerCase())
        ? status
        : status ? `${chip}. ${status}` : chip;
    return said.replace(/[.\s]+$/, '');
}

type PendingConfirm = {
    title: string;
    message: string;
    confirmText: string;
    row: TabRow;
    action: () => Promise<void>;
} | null;

/**
 * Attention order. A list of ten identical-looking rows does not answer the
 * only question the operator actually has, which is "what needs me?". Stopped
 * jobs and finished drafts come first, then what is moving, then what is
 * merely waiting. Ordering is stable inside each band, so rows do not shuffle
 * except when a job genuinely changes state.
 */
const ATTENTION_ORDER: Record<string, number> = {
    failed: 0,
    needs_review: 1,
    processing: 2,
    checking: 2,
    stopped: 2,
    waiting_files: 3,
    queued: 4,
    staged: 5,
};

/** Display states that mean the operator has something to do. */
const NEEDS_OPERATOR = new Set(['failed', 'needs_review']);

/**
 * Sort into attention bands, stable within each. Array.prototype.sort is
 * stable (ES2019+), so jobs in the same band keep queue order and rows only
 * move when a job genuinely changes state.
 */
export function sortByAttention<T extends { state: string; display?: string }>(rows: T[]): T[] {
    const rank = (row: T) => ATTENTION_ORDER[row.display ?? row.state] ?? 9;
    return [...rows].sort((a, b) => rank(a) - rank(b));
}

/**
 * Split a job folder name into the part that identifies the JOB and the part
 * that is reference codes.
 *
 * Real names look like "[08.19][SP33333-QR100003] - Warriewood - Narrabeen" or
 * "05_Chatswood Presbyterian Church". Rendered whole, the codes shout and the
 * place — the thing the operator actually recognises — is pushed onto a second
 * line or off the end. Nothing is discarded: the codes still show, one size
 * down and dimmed, because a strata plan number is real information Francis
 * uses. This is a hierarchy change, not a truncation.
 */
export function splitJobName(name: string): { title: string; ref: string } {
    const raw = String(name || '').trim();
    if (!raw) return { title: '', ref: '' };

    const refs: string[] = [];
    // Leading bracket groups: [08.19][SP33333-QR100003]
    let rest = raw.replace(/^(\s*\[[^\]]*\])+/, (match) => {
        for (const part of match.match(/\[([^\]]*)\]/g) || []) refs.push(part.slice(1, -1).trim());
        return '';
    });
    // A leading job number: "05_Chatswood…" or "7 Neutral Bay…"
    rest = rest.replace(/^\s*[-–—\s]*(\d{1,3})[_.\s-]+/, (_m, num) => { refs.push(String(num)); return ''; });
    // Whatever separator survived between the codes and the name.
    rest = rest.replace(/^[\s_.\-–—]+/, '').trim();

    const title = rest || raw;          // never end up with an empty row
    return { title, ref: refs.filter(Boolean).join(' · ') };
}

/** Operator ceiling. Past this the rail shows "+N" instead of growing. */
const MAX_TABS = 10;

const VISIBLE_STATES = new Set(['staged', 'waiting_files', 'queued', 'processing', 'needs_review', 'failed']);

const DISMISSED_KEY = 'qm_worktabs_dismissed';
const COLLAPSED_KEY = 'qm_worktabs_collapsed';

function killSwitchOn(): boolean {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem('qm_disable_workspace_tabs') === 'true'; }
    catch { return false; }
}

/** projectId → the state it was dismissed AT. */
export function readDismissed(): Record<string, string> {
    try {
        const raw = localStorage.getItem(DISMISSED_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed as Record<string, string> : {};
    } catch { return {}; }
}

function writeDismissed(next: Record<string, string>): void {
    try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(next)); } catch { /* private mode */ }
}

/**
 * Hide the rows the operator closed, but ONLY while they are unchanged.
 *
 * Storing the state the row was dismissed AT is what makes closing safe: if the
 * job moves on — starts running, stops, or produces a draft that needs review —
 * the stored state no longer matches and the row returns by itself. A close can
 * therefore never permanently hide work that later needs attention, which is
 * the failure mode that would make this control dangerous.
 *
 * Exported for the test; the resurface case is awkward to drive through the UI.
 */
export function applyDismissals<T extends { projectId: string; state: string; display?: string }>(
    rows: T[],
    dismissed: Record<string, string>,
): { visible: T[]; stale: string[] } {
    const stale: string[] = [];
    const visible: T[] = [];
    for (const row of rows) {
        const at = dismissed[row.projectId];
        if (at === undefined) { visible.push(row); continue; }
        // The view's display state, so a running job that stops part way
        // comes back although the engine still calls it processing.
        if (at === (row.display ?? row.state)) continue;          // still closed
        stale.push(row.projectId);               // state moved on — resurface
        visible.push(row);
    }
    // A dismissal whose project has left the queue entirely is also stale.
    const live = new Set(rows.map((row) => row.projectId));
    for (const id of Object.keys(dismissed)) if (!live.has(id)) stale.push(id);
    return { visible, stale };
}

/**
 * Cap to MAX_TABS while guaranteeing the open project keeps a slot: a working
 * set that omits the job you are looking at is worse than no working set.
 */
export function capRowsKeepingActive<T extends { projectId: string }>(
    rows: T[],
    currentProjectId: string | null,
    max: number = MAX_TABS,
): { visible: T[]; overflow: number } {
    if (rows.length <= max) return { visible: rows, overflow: 0 };
    const activeIndex = rows.findIndex((row) => row.projectId === currentProjectId);
    if (activeIndex < max) return { visible: rows.slice(0, max), overflow: rows.length - max };
    const head = rows.slice(0, max - 1);
    const activeRow = rows[activeIndex];
    const visible = activeRow ? [...head, activeRow] : head;
    return { visible, overflow: rows.length - visible.length };
}

export default function WorkspaceProjectTabs({
    currentProjectId,
    onOpenProject,
    onOpenStaging,
    projectSummaries,
}: {
    currentProjectId: string | null;
    onOpenProject: (projectId: string, meta: Record<string, unknown>) => void;
    onOpenStaging?: () => void;
    projectSummaries?: any[];
}) {
    const [rows, setRows] = useState<TabRow[]>([]);
    const [dismissed, setDismissed] = useState<Record<string, string>>(() => readDismissed());
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        try { return localStorage.getItem(COLLAPSED_KEY) === 'true'; } catch { return false; }
    });
    const [busyId, setBusyId] = useState<string | null>(null);
    const [actionError, setActionError] = useState<{ projectId: string; message: string } | null>(null);
    const [confirm, setConfirm] = useState<PendingConfirm>(null);
    // The rows last read and this tab's queue modules, so the clock below can
    // judge them again without reading batch_jobs again.
    const sourceRef = useRef<{ live: any[]; queue: any; controls: any } | null>(null);
    // When the rail's words next change with the clock alone (quoteRunRedrawIn).
    const [redrawAt, setRedrawAt] = useState<number | null>(null);
    const disabled = killSwitchOn();
    const projectMetaById = useMemo(() => Object.fromEntries(
        (Array.isArray(projectSummaries) ? projectSummaries : [])
            .filter((project: any) => project?.id)
            .map((project: any) => [String(project.id), project]),
    ), [projectSummaries]);

    // Judge the rows last read against this tab's engine and run, now. Reads
    // no batch_jobs, so the clock can call it (display only).
    const derive = () => {
        const source = sourceRef.current;
        if (!source) return;
        const { live, queue, controls } = source;
        // This tab's engine, so a job it is running reads as running and a
        // job whose page closed reads as stopped (quote-run-view §2.2).
        let engine: any = null;
        try {
            engine = typeof queue?.getQueueStatus === 'function' ? queue.getQueueStatus() : null;
        } catch { engine = null; }
        // This tab's run marker and its earlier pages' runners (A4), the ones
        // the sheet and the job panel read, so the job a reloaded page's own
        // run claimed reads "Stopped part way", never "Running in another tab".
        let run: any = null;
        try {
            run = typeof controls?.readOwnRun === 'function' ? controls.readOwnRun() : null;
        } catch { run = null; }
        const now = Date.now();
        // How many jobs are waiting, so a queue position is only mentioned
        // when something is actually ahead of this one.
        const pendingTotal = live.filter((row: any) => row.state === 'queued').length;
        // Same run-wide ranking the sheet uses; per-phase positions alone
        // let two rows each claim first place. A job waiting for the next
        // run is not ranked in this tab's run.
        const runPositions = assignRunPositions(live as any, engine);

        const next: TabRow[] = live.map((row: any) => {
            // The folder name, as the sheet and RunCard print it (G13): a
            // quote title saved mid-run must not rename the rail entry.
            const name = jobFolderName(row);
            const { title, ref } = splitJobName(name);
            // Derived from the SAME state machine as the Staging Sheet's
            // WAITING ON column (both live in run-blocker.ts), but in the
            // rail's own voice: this surface has no run list, Start button
            // or run counters on screen, so the sheet's "1st in line" and
            // "Not on the run list" refer to things the operator cannot
            // see from here. Same truth, wording that stands alone.
            const { chip, status, display, palette, actionKind } = railWordsFor(row, {
                pendingTotal,
                engine,
                run,
                now,
                runPosition: runPositions.get(String(row.projectId)) ?? null,
            });
            const stateLabel = QUOTE_RUN_DISPLAY[display].label;
            return {
                projectId: row.projectId,
                state: row.state,
                display,
                palette,
                name,
                quoteTitle: jobQuoteTitle(row),
                title,
                ref,
                // A job number ("05") rides the title. A filing code
                // ("08.19 · SP33333-QR100003") does not fit and would push
                // the name out of the way, so it stays in the tooltip.
                shortRef: ref && ref.length <= 4 ? ref : '',
                status: status || stateLabel,
                chip: chip || stateLabel,
                actionKind,
                raw: row,
            };
        });

        setRows(sortByAttention(next));
        const wait = quoteRunRedrawIn(live as any, { engine: engine || { active: false }, run, now });
        setRedrawAt(wait === null ? null : now + wait);
    };

    const refresh = async () => {
        try {
            const queue = await import('../../services/batch-queue');
            const jobs = await queue.listJobs();
            let controls: any = null;
            try {
                controls = await import('../../services/quote-run-controls');
            } catch { controls = null; }
            const list = Array.isArray(jobs) ? jobs : [];
            // The intake-ready proof lives only on this browser's full project
            // record (project-intake-readiness), which the server summaries
            // behind projectMetaById do not carry. Read the same records the
            // Quote Run sheet reads (FileExplorer refreshQueueState), so a job
            // whose files were read gets the sheet's words here: Files read
            // when its saved copy is confirmed, Needs you when it is not.
            // Where the records cannot be read, the proof is unreadable and
            // the view promises no draft for such a job.
            let local: Record<string, any> | null = null;
            try {
                const projectIds = Array.from(new Set(list
                    .map((job: any) => String(job?.projectId || '').trim())
                    .filter(Boolean)));
                const dbModule: any = await import('../../services/db');
                const queueDb = dbModule.default || dbModule.db;
                const records = projectIds.length > 0 ? await queueDb.projects.bulkGet(projectIds) : [];
                local = {};
                (Array.isArray(records) ? records : []).forEach((project: any, index: number) => {
                    if (project) local![projectIds[index]!] = project;
                });
            } catch {
                local = null;
            }
            const collapsedJobs = collapseQueueJobs(
                list,
                local ? { ...projectMetaById, ...local } : projectMetaById,
                { promotionProofReadable: local !== null },
            );
            const live = collapsedJobs.filter((row: any) => VISIBLE_STATES.has(row.state));
            sourceRef.current = { live, queue, controls };
            derive();
        } catch {
            // A failed read leaves the previous rows in place; the live signal
            // retries on the next table write. Never break the workspace.
        }
    };

    useBatchJobsLiveSignal(!disabled, refresh);
    useEffect(() => { if (!disabled) void refresh(); }, [disabled, projectMetaById]);
    // Stop after this job, Keep going, a pause, a resume and the end of a run
    // change this tab's engine and write no batch_jobs row, so the live
    // signal above never fires for them (the next write is the running job's
    // lease heartbeat, up to 20 s later). Judge the rows again on each, as
    // FileExplorer does: a waiting job then stops reading Starts next the
    // moment the stop is armed, on the rail as on the sheet.
    const deriveRef = useRef(derive);
    deriveRef.current = derive;
    useEffect(() => {
        if (disabled) return undefined;
        const onEngineChange = () => {
            try { deriveRef.current(); } catch { /* the rows on screen stay; the next write re-reads */ }
        };
        for (const name of QUOTE_RUN_ENGINE_EVENTS) window.addEventListener(name, onEngineChange);
        return () => {
            for (const name of QUOTE_RUN_ENGINE_EVENTS) window.removeEventListener(name, onEngineChange);
        };
    }, [disabled]);
    // The rail's display clock: when another page's lease runs out, or while
    // a wait counts down, judge the same rows again. It writes nothing and
    // reads no batch_jobs, and the timer is cleared on unmount.
    useEffect(() => {
        if (disabled || redrawAt === null) return undefined;
        const timer = window.setTimeout(() => {
            try { derive(); } catch { /* the rows on screen stay; the next write re-reads */ }
        }, Math.max(0, redrawAt - Date.now()));
        return () => window.clearTimeout(timer);
        // derive reads refs and setters only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, redrawAt]);

    const { visible: undismissed, stale } = applyDismissals(rows, dismissed);

    // Drop dismissals that no longer apply, so the store cannot grow forever
    // and a resurfaced row is not re-hidden on the next render.
    useEffect(() => {
        if (stale.length === 0) return;
        setDismissed((prev) => {
            const next = { ...prev };
            for (const id of stale) delete next[id];
            writeDismissed(next);
            return next;
        });
    }, [stale.join('|')]);

    const { visible, overflow } = capRowsKeepingActive(undismissed, currentProjectId);
    // Counted over the WHOLE undismissed run, not just the visible ten, so the
    // badge cannot under-report because a stopped job sits past the cap.
    const needsMe = undismissed.filter((row) => NEEDS_OPERATOR.has(row.display)).length;

    // ── Alt + Up/Down flips between jobs ───────────────────────────────────
    // Operator: "easy to flip back and forth". Reaching for the mouse and
    // finding the right row is the slow part of switching, so the list gets a
    // keyboard.
    //
    // Alt+ARROWS, NOT Alt+digit: BottomNavBar.tsx:478 already binds Alt+1..5 to
    // stage navigation on this exact page, and a second handler on those keys
    // would fight it silently.
    //
    // Everything current is read through a ref inside a listener bound once.
    // Putting `visible` in the dep array would rebind on every render, and
    // closing over onOpenProject would pin the routing/load chain — the
    // memoised-callback HARD RULE this repo learned the hard way.
    const flipRef = useRef<{ rows: TabRow[]; currentId: string | null; open: typeof onOpenProject }>({
        rows: [], currentId: null, open: onOpenProject,
    });
    flipRef.current = { rows: visible, currentId: currentProjectId, open: onOpenProject };

    useEffect(() => {
        if (disabled) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (!event.altKey || event.ctrlKey || event.metaKey) return;
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
            // Never steal the key from a field the operator is typing in.
            const target = event.target as HTMLElement | null;
            const tag = (target?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;

            const { rows, currentId, open } = flipRef.current;
            if (rows.length < 2) return;
            const at = rows.findIndex((row: TabRow) => row.projectId === currentId);
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const nextIndex = at < 0
                ? (step > 0 ? 0 : rows.length - 1)
                : (at + step + rows.length) % rows.length;
            const next = rows[nextIndex];
            if (!next || next.projectId === currentId) return;
            event.preventDefault();
            // The App-supplied guarded loadProject: it owns the
            // unsaved-changes dialog, so a keyboard flip cannot drop edits
            // any more than a click can.
            open(next.projectId, { title: next.name });
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [disabled]);

    if (disabled || undismissed.length === 0) return null;

    const hideRowForNow = (row: TabRow) => {
        setDismissed((prev) => {
            const next = { ...prev, [row.projectId]: row.display };
            writeDismissed(next);
            return next;
        });
    };

    const runRowAction = async (row: TabRow, action: () => Promise<void>) => {
        if (busyId) return;
        setBusyId(row.projectId);
        setActionError(null);
        try {
            await action();
            await refresh();
        } catch (error: any) {
            setActionError({
                projectId: row.projectId,
                message: error?.message || 'Quote Run could not be updated. Open Import Projects and try again.',
            });
        } finally {
            setBusyId(null);
        }
    };

    const takeOffRun = (row: TabRow) => {
        void runRowAction(row, async () => {
            const { unqueueToStaged } = await import('../../services/batch-queue');
            const phase = row.raw?.effectiveJob?.phase || 'prepare-quote';
            const result = await unqueueToStaged(row.projectId, { phase });
            if (!result?.changed) {
                throw new Error('This job changed before it could be taken off the run. Open Import Projects to review its current status.');
            }
        });
    };

    const askToRemoveStaged = (row: TabRow) => {
        setConfirm({
            title: `Remove the staged setup for “${row.name}”?`,
            message: 'The staged file selection and any waiting-for-files status are removed from Quote Run. Files on disk and any saved QuoteMaker project are untouched.',
            confirmText: 'Remove staged setup',
            row,
            action: async () => {
                const { removeStaged } = await import('../../services/batch-queue');
                const phase = row.raw?.effectiveJob?.phase || 'prepare-quote';
                const result = await removeStaged(row.projectId, { phase });
                if (!result?.removed) {
                    throw new Error('This staged setup changed before it could be removed. Open Import Projects to review its current status.');
                }

                // The durable queue row is already gone. Removing it from the
                // current paint as well avoids a stale live-query frame making
                // a permanent removal look temporary.
                setRows((prev) => prev.filter((candidate) => candidate.projectId !== row.projectId));
                setDismissed((prev) => {
                    if (!(row.projectId in prev)) return prev;
                    const next = { ...prev };
                    delete next[row.projectId];
                    writeDismissed(next);
                    return next;
                });
            },
        });
    };

    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            try { localStorage.setItem(COLLAPSED_KEY, String(!prev)); } catch { /* private mode */ }
            return !prev;
        });
    };

    return (
        <nav
            aria-label="Quote Run jobs"
            className={`qm-worktabs no-print${collapsed ? ' qm-worktabs--collapsed' : ''}`}
            data-qm-worktabs-count={visible.length}
        >
            <div className="qm-worktabs__head">
                <span className="qm-worktabs__label">{`QUOTE RUN · ${undismissed.length}`}</span>
                {/* The one number worth reading from across the room. Rendered
                    only when it is non-zero, so an ordinary run shows no badge
                    at all rather than a permanent "0 need you". */}
                {needsMe > 0 && (
                    <span
                        className="qm-worktabs__needs"
                        title={`${needsMe} job${needsMe === 1 ? '' : 's'} stopped or waiting on you`}
                    >
                        {needsMe}
                    </span>
                )}
                {/* A shortcut nobody knows about is not a shortcut. Shown only
                    when there is more than one job to flip between, and only
                    when the list is wide enough to say it. */}
                {!collapsed && visible.length > 1 && (
                    <span className="qm-worktabs__hint" aria-hidden="true">ALT ↑↓</span>
                )}
                <button
                    type="button"
                    className="qm-worktabs__toggle qm-inline-action-btn"
                    aria-expanded={!collapsed}
                    title={collapsed ? 'Show job names' : 'Collapse to leave more room for the quote'}
                    aria-label={collapsed ? 'Show job names' : 'Collapse the job list'}
                    onClick={toggleCollapsed}
                >
                    {collapsed
                        ? <Icons.ChevronRight size={14} aria-hidden="true" />
                        : <Icons.ChevronLeft size={14} aria-hidden="true" />}
                </button>
            </div>

            <div className="qm-worktabs__list">
                {visible.map((row) => {
                    const tone = quoteRunColours(row.palette);
                    const active = row.projectId === currentProjectId;
                    const said = railWords(row);
                    return (
                        <div
                            key={row.projectId}
                            className={`qm-worktab${active ? ' qm-worktab--active' : ''}`}
                            data-qm-workspace-tab={row.projectId}
                            data-qm-workspace-tab-state={row.state}
                            data-qm-workspace-tab-display={row.display}
                        >
                            <div className="qm-worktab__main">
                                <button
                                    type="button"
                                    // qm-inline-action-btn opts out of the global
                                    // button theme's 44px min-height/width, which
                                    // would otherwise burst a compact rail row.
                                    className="qm-worktab__open qm-inline-action-btn"
                                    aria-current={active ? 'page' : undefined}
                                    // State is carried by dot COLOUR, and colour
                                    // alone is not an accessible signal, so it also
                                    // rides the label, in the view's word (the
                                    // sheet's chip). The full folder name carries
                                    // any filing code that no longer has a line of
                                    // its own, so nothing is unreachable.
                                    aria-label={`${row.name}. ${said}${active ? '. Open' : ''}`}
                                    title={`${row.name}${row.quoteTitle ? `\n${row.quoteTitle}` : ''}\n${said}${active ? '\n(open)' : ''}`}
                                    onClick={() => { if (!active) onOpenProject(row.projectId, { title: row.name }); }}
                                >
                                    <span
                                        className="qm-worktab__dot"
                                        aria-hidden="true"
                                        style={{ background: tone.border }}
                                    />
                                    <span className="qm-worktab__text">
                                        <span className="qm-worktab__title">
                                            {row.shortRef && <span className="qm-worktab__num">{row.shortRef}</span>}
                                            {row.title}
                                        </span>
                                        <span className="qm-worktab__status">{row.status}</span>
                                    </span>
                                </button>
                            </div>

                            <div className="qm-worktab__actions" aria-label={`Actions for ${row.name}`}>
                                {/* The sheet's Leave out, and only where the
                                    sheet offers it: never on a job whose files
                                    were read, one that needs you, or one whose
                                    saved copy is not confirmed. */}
                                {row.actionKind === 'leave-out' && (
                                    <button
                                        type="button"
                                        className="qm-worktab__action qm-worktab__action--run qm-inline-action-btn"
                                        aria-label={`Take ${row.name} off the run list`}
                                        title="Moves this job back to staged. Its file selection is kept, and you can add it to the run again."
                                        disabled={busyId === row.projectId}
                                        onClick={(event) => { event.stopPropagation(); takeOffRun(row); }}
                                    >
                                        <Icons.Undo size={11} aria-hidden="true" />
                                        <span>{busyId === row.projectId ? 'UPDATING…' : 'TAKE OFF RUN'}</span>
                                    </button>
                                )}
                                {(row.state === 'staged' || row.state === 'waiting_files') && (
                                    <button
                                        type="button"
                                        className="qm-worktab__action qm-worktab__action--remove qm-inline-action-btn"
                                        aria-label={`Remove the staged setup for ${row.name}`}
                                        title="Removes this staged file selection from Quote Run. Files on disk and any saved project are untouched."
                                        disabled={busyId === row.projectId}
                                        onClick={(event) => { event.stopPropagation(); askToRemoveStaged(row); }}
                                    >
                                        <Icons.Trash size={11} aria-hidden="true" />
                                        <span>REMOVE STAGING</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="qm-worktab__action qm-worktab__action--hide qm-inline-action-btn"
                                    aria-label={`Hide ${row.name} from this rail until its status changes`}
                                    title="Only hides this rail item. Quote Run is unchanged, and the item returns if its status changes."
                                    disabled={busyId === row.projectId}
                                    onClick={(event) => { event.stopPropagation(); hideRowForNow(row); }}
                                >
                                    <Icons.EyeOff size={11} aria-hidden="true" />
                                    <span>HIDE FOR NOW</span>
                                </button>
                            </div>
                            {actionError?.projectId === row.projectId && (
                                <span className="qm-worktab__error" role="status">{actionError.message}</span>
                            )}
                        </div>
                    );
                })}
            </div>

            {overflow > 0 && (
                <button
                    type="button"
                    className="qm-worktabs__more qm-inline-action-btn"
                    title={`${overflow} more job${overflow === 1 ? '' : 's'} in the run. Open Import Projects to see all of them.`}
                    onClick={() => { if (onOpenStaging) onOpenStaging(); }}
                >
                    {collapsed ? `+${overflow}` : `+${overflow} more`}
                </button>
            )}
            {onOpenStaging && (
                <button
                    type="button"
                    className="qm-worktabs__manage qm-inline-action-btn"
                    aria-label="Open Quote Run manager"
                    title="Open Import Projects to manage every staged, queued and completed job."
                    onClick={onOpenStaging}
                >
                    <Icons.ListOrdered size={13} aria-hidden="true" />
                    <span>MANAGE QUOTE RUN</span>
                </button>
            )}

            <ConfirmModal
                isOpen={!!confirm}
                onClose={() => setConfirm(null)}
                onCancel={() => setConfirm(null)}
                onConfirm={() => {
                    const pending = confirm;
                    setConfirm(null);
                    if (pending) void runRowAction(pending.row, pending.action);
                }}
                title={confirm?.title}
                message={confirm?.message}
                confirmText={confirm?.confirmText}
                cancelText="Keep staged setup"
                confirmColor="red"
                icon="danger"
                zIndex={10000}
            />
        </nav>
    );
}
